import moment from 'moment';

/**
 * Фиксированная палитра заливок групповых слотов на таймлайне (solid + белый текст).
 * Индекс = sectionGroupId % length — одинаково у всех админов, без БД/миграции.
 * Не пересекаем intentionally с chip аренды (#EF4444 / #F59E0B) и персоналки (#2563EB).
 */
export const SECTION_GROUP_SLOT_PALETTE = [
  '#0E935F', // brand green
  '#7C3AED', // violet
  '#DB2777', // pink
  '#EA580C', // orange
  '#0F766E', // teal
  '#4F46E5', // indigo
  '#B45309', // amber dark
  '#BE185D', // rose
  '#059669', // emerald
  '#9333EA', // purple
  '#C2410C', // burnt orange
  '#155E75', // cyan dark
  '#6D28D9', // deep violet
  '#9A3412', // rust
  '#1D4ED8', // blue (отличный от personal #2563EB)
];

const SECTION_GROUP_SLOT_FG = '#ffffff';

/**
 * Стабильный цвет чипа группового занятия по id группы.
 * Без валидного groupId — null (caller использует fallback «секция»).
 */
export function toneForSectionGroupId(sectionGroupId) {
  const id = Number(sectionGroupId);
  if (!Number.isFinite(id) || id <= 0) return null;
  const palette = SECTION_GROUP_SLOT_PALETTE;
  const bg = palette[id % palette.length];
  return { bg, fg: SECTION_GROUP_SLOT_FG };
}

/** Парсит StartAt/EndAt occupancy: в БД UTC, JSON часто без Z → иначе слоты уезжают на 3ч. */
export function parseOccurrenceMoment(raw) {
  if (raw == null || raw === '') return moment.invalid();
  if (typeof raw === 'string') {
    const s = raw.trim();
    const hasTz = /([zZ]|[+-]\d{2}:?\d{2})$/.test(s);
    if (hasTz) return moment(s);
    return moment.utc(s).local();
  }
  return moment(raw);
}

export function normalizeOccurrenceEvent(occ) {
  if (!occ) return null;
  const kind = occ.eventKind || occ.EventKind || 'section_occurrence';
  const sectionName = occ.sectionName || occ.SectionName;
  const sectionGroupName = occ.sectionGroupName || occ.SectionGroupName;
  const label =
    (kind === 'personal_training'
      ? (sectionName ? `Персональная: ${sectionName}` : 'Персональная тренировка')
      : null) ||
    sectionGroupName ||
    (sectionName ? `${sectionName}${sectionGroupName ? ` / ${sectionGroupName}` : ''}` : null) ||
    (occ.sectionGroupId != null && occ.sectionGroupId !== 0 ? `Группа #${occ.sectionGroupId}` : null) ||
    (occ.sectionId != null ? `Секция #${occ.sectionId}` : null) ||
    'Занятие секции';
  return {
    id: occ.id != null ? occ.id : occ.Id,
    kind,
    label,
    sectionId: occ.sectionId != null ? occ.sectionId : occ.SectionId,
    sectionName,
    sectionGroupId: occ.sectionGroupId != null ? occ.sectionGroupId : occ.SectionGroupId,
    sectionGroupName,
    startAt: occ.startAt || occ.StartAt,
    endAt: occ.endAt || occ.EndAt,
    playgroundId: occ.playgroundId != null ? occ.playgroundId : occ.PlaygroundId,
    trainerId: occ.trainerId != null ? occ.trainerId : occ.TrainerId,
    bookedCount: occ.bookedCount != null ? occ.bookedCount : occ.BookedCount,
    maxParticipants: occ.maxParticipants != null ? occ.maxParticipants : occ.MaxParticipants,
  };
}

/** Разбивает occurrence на 30-мин слоты для сопоставления с сеткой админки */
export function flattenOccurrenceToCells(occ, parseMoment = parseOccurrenceMoment) {
  const event = normalizeOccurrenceEvent(occ);
  if (!event) return [];
  const { kind, label } = event;
  const startM = parseMoment(event.startAt);
  const endM = parseMoment(event.endAt);
  if (!startM.isValid() || !endM.isValid()) return [];
  const cells = [];
  const pushCell = (m) => {
    const day = m.format('YYYY-MM-DD');
    const interval = m.hours() * 2 + (m.minutes() >= 30 ? 1 : 0);
    cells.push({ day, interval, label, kind, event });
  };
  let cur = startM.clone();
  if (!cur.isBefore(endM)) {
    pushCell(cur);
    return cells;
  }
  while (cur.isBefore(endM)) {
    pushCell(cur);
    cur.add(30, 'minutes');
  }
  return cells;
}

export function buildSectionOccurrenceMap(occurrences, playgroundId, flatten = flattenOccurrenceToCells) {
  const map = {};
  (occurrences || []).forEach((occ) => {
    if (occ.playgroundId != null && Number(occ.playgroundId) !== Number(playgroundId)) return;
    flatten(occ).forEach(({ day, interval, label, kind, event }) => {
      const k = `${day}::${interval}`;
      const prev = map[k];
      if (!prev) {
        map[k] = { label, kind, event };
      } else {
        map[k] = {
          label: `${prev.label}; ${label}`,
          kind: prev.kind === 'personal_training' || kind === 'personal_training'
            ? 'personal_training'
            : 'section_occurrence',
          event: prev.event || event,
        };
      }
    });
  });
  return map;
}

/** Границы занятия на 30-мин сетке (первый и последний занятые интервалы). */
export function getOccurrenceIntervalBounds(event, flatten = flattenOccurrenceToCells) {
  const cells = flatten(event);
  if (!cells.length) return null;
  const first = cells[0];
  const last = cells[cells.length - 1];
  return {
    startDay: first.day,
    startInterval: Number(first.interval),
    endDay: last.day,
    endInterval: Number(last.interval),
    cellCount: cells.length,
  };
}

/**
 * Режим DnD: верхний слот → resize-start, нижний → resize-end, середина → move.
 * Для одного слота зона определяется по Y внутри ячейки (верх / низ / середина).
 */
export function resolveSectionDragMode(bounds, day, interval, pointerRatioY) {
  if (!bounds) return 'move';
  const isFirst =
    day === bounds.startDay && Number(interval) === Number(bounds.startInterval);
  const isLast =
    day === bounds.endDay && Number(interval) === Number(bounds.endInterval);

  if (isFirst && isLast) {
    const r = Number(pointerRatioY);
    if (!Number.isNaN(r)) {
      if (r < 0.35) return 'resize-start';
      if (r > 0.65) return 'resize-end';
    }
    return 'move';
  }
  if (isFirst) return 'resize-start';
  if (isLast) return 'resize-end';
  return 'move';
}

/** Курсор подсказки для режима на ячейке. */
export function sectionDragCursorForMode(mode) {
  if (mode === 'resize-start' || mode === 'resize-end') return 'ns-resize';
  return 'grab';
}

/** Склейка только для одного занятия/брони; пересечения двух событий не объединяем. */
export function getOccupancyMergeKey(occupancy) {
  if (!occupancy || !occupancy.event) return null;
  if (occupancy.label && String(occupancy.label).indexOf(';') !== -1) return null;
  const ev = occupancy.event;
  if (ev.id != null && ev.id !== '') return `occ:${ev.id}`;
  if (ev.startAt && ev.endAt) {
    return `occ:${ev.startAt}|${ev.endAt}|${ev.label || ''}`;
  }
  return null;
}

export function getIntervalBookingMergeId(intervalData) {
  if (!intervalData || !Array.isArray(intervalData.values) || intervalData.values.length !== 1) {
    return null;
  }
  const bookings = intervalData.values[0].bookings;
  if (!Array.isArray(bookings) || bookings.length !== 1) return null;
  const booking = bookings[0];
  const id = booking.id != null ? booking.id : booking.Id;
  return id == null ? null : String(id);
}

/**
 * Ключи вертикальной склейки на 48 слотов дня.
 * Occupancy важнее аренды. Площадки 1/2 и 1/3 не склеиваем.
 */
export function buildDayMergeKeys(day, hourArray, getOccupancyAt) {
  const keys = [];
  for (let i = 0; i < 48; i += 1) {
    const intervalData = hourArray && hourArray[i];
    if (intervalData && Array.isArray(intervalData.values) && intervalData.values.length > 1) {
      keys.push(null);
      continue;
    }
    const occ = getOccupancyAt ? getOccupancyAt(day, i) : null;
    const occKey = getOccupancyMergeKey(occ);
    if (occKey) {
      keys.push(occKey);
      continue;
    }
    const bookId = getIntervalBookingMergeId(intervalData);
    keys.push(bookId ? `book:${bookId}` : null);
  }
  return keys;
}

/** По массиву ключей строит rowspan / skip для колонки таблицы. */
export function buildColumnRowspans(keys) {
  const list = keys || [];
  const n = list.length;
  const out = new Array(n);
  let i = 0;
  while (i < n) {
    const key = list[i];
    if (!key) {
      out[i] = { skip: false, rowspan: 1, mergeKey: null };
      i += 1;
      continue;
    }
    let j = i + 1;
    while (j < n && list[j] === key) j += 1;
    out[i] = { skip: false, rowspan: j - i, mergeKey: key };
    for (let k = i + 1; k < j; k += 1) {
      out[k] = { skip: true, rowspan: 0, mergeKey: key };
    }
    i = j;
  }
  return out;
}

/** Какой 30-мин слот внутри объединённой ячейки соответствует позиции курсора. */
export function intervalOffsetInSpan(ratioY, rowspan) {
  const span = Math.max(1, Number(rowspan) || 1);
  const r = Number(ratioY);
  if (Number.isNaN(r)) return 0;
  return Math.min(span - 1, Math.max(0, Math.floor(r * span)));
}

/** Слот по clientY внутри прямоугольника склеенной ячейки (в т.ч. сжатие за уголок). */
export function intervalFromClientYInSpan(clientY, rectTop, rectHeight, startInterval, rowspan) {
  const start = Number(startInterval);
  const height = Number(rectHeight);
  if (Number.isNaN(start)) return 0;
  if (!height || Number.isNaN(height)) return start;
  const ratio = Math.min(1, Math.max(0, (Number(clientY) - Number(rectTop)) / height));
  return start + intervalOffsetInSpan(ratio, rowspan);
}

/**
 * resize-end: конец не раньше текущего начала (сжатие внутри блока).
 * resize-start: начало не позже текущего конца.
 * Растягивание за исходные границы не режется.
 */
export function clampResizeDropInterval(mode, dropInterval, startInterval, endInterval) {
  const drop = Number(dropInterval);
  const start = Number(startInterval);
  const end = Number(endInterval);
  if (Number.isNaN(drop)) return Number.isNaN(start) ? 0 : start;
  if (mode === 'resize-end' && !Number.isNaN(start)) return Math.max(start, drop);
  if (mode === 'resize-start' && !Number.isNaN(end)) return Math.min(end, drop);
  return drop;
}

/** Новый диапазон слотов после resize (inclusive start/end). */
export function computeResizedSlotRange(mode, startInterval, endInterval, dropInterval) {
  const start = Number(startInterval);
  const end = Number(endInterval);
  const drop = clampResizeDropInterval(mode, dropInterval, start, end);
  if (mode === 'resize-end') {
    return { startInterval: start, endInterval: drop, cellCount: drop - start + 1 };
  }
  if (mode === 'resize-start') {
    return { startInterval: drop, endInterval: end, cellCount: end - drop + 1 };
  }
  return { startInterval: start, endInterval: end, cellCount: end - start + 1 };
}

/**
 * Живое превью сжатия склеенного блока: высота доли оставшихся слотов.
 * Растягивание (cellCount >= rowspan) не клипует исходную ячейку.
 */
export function mergedBlockResizePreview(mode, blockStart, blockEnd, dropInterval, rowspan) {
  const span = Math.max(1, Number(rowspan) || 1);
  const start = Number(blockStart);
  const end = Number(blockEnd);
  if (mode !== 'resize-end' && mode !== 'resize-start') {
    return {
      active: false,
      heightRatio: 1,
      align: 'top',
      startInterval: start,
      endInterval: end,
      cellCount: span,
    };
  }
  const range = computeResizedSlotRange(mode, start, end, dropInterval);
  const shrink = range.cellCount > 0 && range.cellCount < span;
  return {
    active: shrink,
    heightRatio: shrink ? range.cellCount / span : 1,
    align: mode === 'resize-start' ? 'bottom' : 'top',
    startInterval: range.startInterval,
    endInterval: range.endInterval,
    cellCount: range.cellCount,
  };
}

/**
 * Режим DnD внутри уже склеенного блока (одна DOM-ячейка на N слотов).
 * Края — растягивание, середина — перенос.
 */
export function resolveMergedBlockDragMode(ratioY, rowspan, handle) {
  if (handle === 'end') return 'resize-end';
  if (handle === 'start') return 'resize-start';
  const span = Math.max(1, Number(rowspan) || 1);
  const r = Number(ratioY);
  if (Number.isNaN(r)) return 'move';
  const edge = span <= 1 ? 0.35 : 0.18;
  if (r < edge) return 'resize-start';
  if (r > 1 - edge) return 'resize-end';
  return 'move';
}
